import { Group, Stack, Text, useMantineColorScheme } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ChartPoint } from "../ChartPoint/ChartPoint";

import { getColorFromName } from "@/components/elements/PerformanceChart/utils";

import type { AuctionCarTransaction } from "@/types/auctions";
import type { ChartPointShape } from "@/utils/transactionRecord";
import type { FC } from "react";

const CAR_TYPE_ITEMS: { key: "normal" | "outlier" | "replica"; shape: ChartPointShape }[] = [
  { key: "normal", shape: "rectangle" },
  { key: "outlier", shape: "triangle" },
  { key: "replica", shape: "square" },
];

const CONDITION_KEYS: AuctionCarTransaction["condition"][] = [
  "superb",
  "excellent",
  "good",
  "fair",
  "not_available",
  "project",
];

function LegendShapeIcon({ color, shape }: { color: string; shape: ChartPointShape }) {
  return (
    <svg width={16} height={16} aria-hidden>
      <ChartPoint cx={8} cy={8} fill={color} shape={shape} size={5} />
    </svg>
  );
}

function LegendColorIcon({ color }: { color: string }) {
  return (
    <svg width={16} height={16} aria-hidden>
      <circle cx={8} cy={8} r={5} fill={color} />
    </svg>
  );
}

export const ChartLegend: FC = () => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();

  const isDark = colorScheme === "dark";

  return (
    <Group align="flex-start" c={isDark ? "#A7A7A7" : undefined} gap="xl" p="md" wrap="nowrap">
      <Stack gap="sm">
        <Text fw={600} size="sm">
          {t("components.AuctionsChart.legend.car_type")}
        </Text>
        <Stack gap="sm">
          {CAR_TYPE_ITEMS.map(({ key, shape }) => (
            <Group key={key} gap="xs" wrap="nowrap">
              <LegendShapeIcon color="var(--mantine-color-white)" shape={shape} />
              <Text size="sm">{t(`components.AuctionsChart.legend.${key}`)}</Text>
            </Group>
          ))}
        </Stack>
      </Stack>

      <Stack gap="sm">
        <Text fw={600} size="sm">
          {t("components.AuctionsChart.legend.conditions")}
        </Text>
        <Stack gap="sm">
          {CONDITION_KEYS.map((condition) => (
            <Group key={condition} gap="xs" wrap="nowrap">
              <LegendColorIcon color={getColorFromName(condition) ?? "#94a3b8"} />
              <Text size="sm">{t(`car.conditions.${condition}`, condition)}</Text>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Group>
  );
};
